import { ValidateFields } from 'rc-field-form/es/interface';
import React, { useCallback, useMemo } from 'react';
import AddNewItemBtn from './components/AddNewFieldButton';
import RestaurantsMultipleSelect from './components/RestaurantsMultipleSelect/RestaurantsMultipleSelect';
import PriceField from './components/PriceField';
import SelectedItem from 'components/SelectedItem/SelectedItem';
import { IItemForGetShortInfoWithStatus, ITotalPrice, EntityStatus } from 'types';
import style from './RestaurantsField.module.scss';

interface IRestaurantsField {
	addPrices: (value: ITotalPrice[]) => void;
	removePrice: (value: string[]) => void;
	setRestaurantsForPrice: (value: string[]) => void;
	setFetchedRestaurants: (value: IItemForGetShortInfoWithStatus[]) => void;
	changePrice: (value: string) => void;
	selectedCustomer: Nullable<string>;
	restaurantsForPrice: string[];
	fetchedRestaurants: IItemForGetShortInfoWithStatus[];
	totalPrices: ITotalPrice[];
	price: string;
	revalidateForm: ValidateFields;
}

const RestaurantsField: React.FC<IRestaurantsField> = ({
	fetchedRestaurants,
	restaurantsForPrice,
	totalPrices,
	selectedCustomer,
	price,
	setFetchedRestaurants,
	setRestaurantsForPrice,
	removePrice,
	changePrice,
	addPrices,
	revalidateForm,
}) => {
	const disabledButton = !restaurantsForPrice.length || !price;

	const restaurantsList = useMemo(() => {
		return totalPrices.map((el) => (
			<SelectedItem
				key={el.restaurantId}
				label={[el.restaurantName, el.price]}
				onDelete={() => removePrice([el.restaurantId])}
			/>
		));
	}, [totalPrices, removePrice]);

	const handleAddPrice = useCallback(() => {
		const prices: ITotalPrice[] = restaurantsForPrice.map((el) => {
			const fetchedRestaurant = fetchedRestaurants.find((rest) => rest.id === el);
			return {
				restaurantId: el,
				restaurantName: fetchedRestaurant?.name || '',
				restaurantStatus: fetchedRestaurant?.status || EntityStatus.Active,
				price,
			};
		});
		prices.forEach((el) => {
			if (!totalPrices.some((total) => total.restaurantId === el.restaurantId)) {
				addPrices([el]);
				revalidateForm();
			}
		});
	}, [restaurantsForPrice, fetchedRestaurants, price, totalPrices, addPrices]);

	return (
		<div className={style.container} data-test="RestaurantsField">
			<div className={style.field}>
				<RestaurantsMultipleSelect
					restaurantsForPrice={restaurantsForPrice}
					selectedCustomer={selectedCustomer}
					totalPrices={totalPrices}
					setRestaurantsForPrice={setRestaurantsForPrice}
					setFetchedRestaurants={setFetchedRestaurants}
					removePrice={removePrice}
				/>
				<PriceField restaurantsForPrice={restaurantsForPrice} changePrice={changePrice} />
				<AddNewItemBtn onClick={handleAddPrice} isDisabled={disabledButton} />
			</div>
			<div className={style.pricesList}>{restaurantsList}</div>
		</div>
	);
};

export default React.memo(RestaurantsField);
